import { useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { forwardRef } from '@chakra-ui/react';
import { IMask, useIMask } from 'react-imask';
import { DateTime } from 'luxon';
import DatePicker, { DateRange } from '../../DatePicker/DatePicker';
import IconButton from '../../IconButton/IconButton';
import Input, { InputProps } from '../Input/Input';

export type DateInputError = 'outOfRange';

export type DateInputProps = Omit<InputProps, 'value' | 'onChange' | 'placeholder' | 'onError'> & {
  onChange?(value: string): void;
  onError?(error: DateInputError, message: string): void;
  value?: string;
  selectable?: DateRange;
};

export const dateInputDateFormat = 'MM/dd/yyyy';

const DateInput = forwardRef<DateInputProps, 'div'>((props, ref) => {
  const { inputRef, onChange, onError, selectable, value, ...rest } = props;
  const [isDatePickerVisible, setIsDatePickerVisible] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);

  const {
    ref: innerRef,
    setValue,
    value: currentValue,
  } = useIMask<HTMLInputElement>(
    {
      autofix: 'pad',
      blocks: {
        dd: {
          from: 1,
          mask: IMask.MaskedRange,
          placeholderChar: 'd',
          to: 31,
        },
        LL: {
          from: 1,
          mask: IMask.MaskedRange,
          placeholderChar: 'm',
          to: 12,
        },
        yyyy: {
          autofix: false,
          from: 0,
          mask: IMask.MaskedRange,
          placeholderChar: 'y',
          to: 9999,
        },
      },
      format: (date: Date | null) => (date ? DateTime.fromJSDate(date).toFormat(dateInputDateFormat) : ''),
      lazy: !isFocused && isEmpty,
      mask: Date,
      overwrite: true,
      parse: (str: string) => {
        try {
          const parsedDate = DateTime.fromFormat(str, dateInputDateFormat);
          if (selectable && !selectable.containsEqual(parsedDate)) {
            onError?.('outOfRange', 'Date is not selectable.');
          }
          return parsedDate.toJSDate();
        } catch {
          return DateTime.now().toJSDate();
        }
      },
      pattern: 'LL/`dd/`yyyy',
    },
    {
      onAccept: (val, maskRef) => {
        onChange?.(val);
        setIsEmpty(!maskRef.unmaskedValue);
      },
    },
  );

  useImperativeHandle(inputRef, () => innerRef.current!, []);

  useEffect(() => setValue(value || ''), [value]);

  const currentDate = useMemo(() => {
    try {
      return DateTime.fromFormat(currentValue, dateInputDateFormat);
    } catch {
      return undefined;
    }
  }, [currentValue]);

  return (
    <DatePicker
      mode="day"
      onApply={(day) => {
        const newVal = day?.toFormat(dateInputDateFormat) || '';
        onChange?.(newVal);
        setValue(newVal);
      }}
      onClose={() => setIsDatePickerVisible(false)}
      selectable={selectable}
      selected={currentDate}
      variant="default"
      visible={isDatePickerVisible}
    >
      <Input
        {...rest}
        ref={ref}
        inputRef={innerRef}
        onBlur={() => setIsFocused(false)}
        onFocus={() => setIsFocused(true)}
        placeholder={dateInputDateFormat.toLocaleLowerCase()}
        rightAddon={
          <IconButton
            _active={{ background: 'transparent' }}
            _hover={{ background: 'transparent' }}
            aria-label="Show date picker"
            color="icon.tertiary"
            iconName="Calendar"
            onClick={() => setIsDatePickerVisible(!isDatePickerVisible)}
            variant="tertiary"
          />
        }
        rightAddonPlacement="inside"
      />
    </DatePicker>
  );
});

export default DateInput;
